import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useAuth } from '../auth/AuthProvider';
import useApi from '@/hooks/useApi';
import useAnalytics from '@/hooks/useAnalytics';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getLanguage,
  resolveSupportedLanguage,
} from '@/constants/languages';
import { changeLanguage } from '@/i18n';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

interface HeaderLanguageSelectorProps {
  /**
   * Styles merged onto the Select root. The built-in top offset aligns the chip
   * with the toolbar's Sign In pill; a caller that lays the chip out in an
   * already-centered row cancels it with `{ '&&': { marginTop: 0 } }` rather
   * than reaching into the MUI class names from outside.
   */
  sx?: SxProps<Theme>;
}

export function HeaderLanguageSelector({ sx }: HeaderLanguageSelectorProps = {}) {
  const { t, i18n } = useTranslation('common');
  const { isAuthenticated, user } = useAuth();
  const query = useApi();
  const analytics = useAnalytics();
  const currentCode = resolveSupportedLanguage(i18n.language) ?? DEFAULT_LANGUAGE;
  const currentRow = getLanguage(currentCode);

  const handleSelect = async (code: string) => {
    if (code === i18n.language) return;

    changeLanguage(code);
    analytics.track('Language Updated', { source: 'header', language: code });

    if (isAuthenticated && user?.email) {
      try {
        await query('/user/settings', {
          method: 'POST',
          body: JSON.stringify({
            email: user.email,
            preferred_language: code,
            // Explicit null tells the PII repository to skip this field —
            // the Pydantic default is "" which would otherwise clear it.
            phone_number: null,
          }),
        });
      } catch {
        // Best-effort — i18n + localStorage already updated above.
      }
    }
  };

  return (
    <FormControl size="small">
      <Select
        value={currentCode}
        onChange={(e) => handleSelect(e.target.value)}
        // Endonym so a screen reader announces "Language: 简体中文" rather than
        // the one-glyph chip (FR-25), plus the chip's own label because WCAG
        // 2.5.3 (Label in Name) needs the visible text in the accessible name —
        // "RU" doesn't occur in "Русский", nor "PT" in "Português".
        inputProps={{
          'aria-label': currentRow
            ? `${t('language')}: ${currentRow.label} (${currentRow.short})`
            : `${t('language')}: ${currentCode}`,
        }}
        IconComponent={KeyboardArrowDownRoundedIcon}
        // Compact, rounded "chip" that reads as a secondary control next to
        // the primary Sign In / Sign Up pill: subtle frame, globe glyph, and
        // the registry's `short` label. The dropdown lists endonyms.
        renderValue={(code) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Class + ``sx`` rather than an inline style so a caller can size
                the glyph to its own label — the landing chip runs a 10px code
                where 18px reads oversized. */}
            <Box
              component={GlobeAltIcon}
              className="vm-lang-globe"
              sx={{ width: 18, height: 18, color: semanticColors.textTertiary }}
              aria-hidden="true"
            />
            {/* `short`, not `code.toUpperCase()` — that produced "ZH-HANS", and
                a script's own glyph (简 / ع) reads faster to the reader who
                needs it. Keep this in sync with the `aria-label` above. */}
            {getLanguage(String(code))?.short ?? String(code).toUpperCase()}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: '4px',
              borderRadius: BORDER_RADIUS,
              boxShadow: `0 4px 16px ${semanticColors.shadowLight}`,
            },
          },
        }}
        sx={[
          {
            borderRadius: '999px',
            fontSize: { xs: '11px', md: '13px' },
            fontWeight: 600,
            // Match the toolbar Sign In / Sign Up pill's height and top offset
            // so the two controls sit on the same horizontal line.
            minHeight: '32px',
            '&&': { marginTop: { xs: '6px', md: '8px' } },
            color: semanticColors.textBody,
            backgroundColor: semanticColors.surface,
            // ``inputBorderHover`` (#707070) keeps the chip frame >=3:1 against
            // both the white fill and the light-blue header (WCAG 1.4.11) so the
            // control's boundary is perceivable, not just decorative.
            boxShadow: `inset 0 0 0 1px ${semanticColors.inputBorderHover}`,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              paddingTop: '5px',
              paddingBottom: '5px',
              paddingLeft: { xs: '8px', md: '12px' },
              // Clears the glyph below: it is 20px wide and sits 6px from the
              // right edge, so the label needs at least 26px here to stay off
              // it. At the previous 26px/24px pairing the chevron rendered on
              // top of the language code at xs.
              paddingRight: { xs: '28px !important', md: '32px !important' },
              lineHeight: 1.4,
            },
            '& .MuiSelect-icon': {
              color: semanticColors.textTertiary,
              right: '6px',
              // MUI's default 24px glyph is oversized beside an 11-13px code,
              // and was wide enough to sit under the label.
              fontSize: '20px',
            },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover': {
              backgroundColor: semanticColors.surfaceSubtle,
              boxShadow: `inset 0 0 0 1px ${semanticColors.textTertiary}`,
            },
            '&.Mui-focusVisible, &:has(:focus-visible)': {
              boxShadow: `inset 0 0 0 1px ${semanticColors.brandPrimary}, 0 0 0 2px ${semanticColors.brandPrimary}`,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {SUPPORTED_LANGUAGES.map(({ code, label }) => (
          <MenuItem key={code} value={code} lang={code} sx={{ fontSize: '14px' }}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
